/**
 * client/src/sync/useRoomSync.ts — the room, in React, and §7.4's two drift policies.
 *
 * design.md §7.3–§7.5. `room.ts` is the transport and the arithmetic; this file is the part that has
 * to live inside a component tree: the socket's state, the 250 ms correction loop, the autoplay
 * gesture, and the blocking reasons a member reports about themselves.
 *
 * ── The two modes are two different products and share one socket ───────────
 * **`listen`** is movies, retuned. A shared clock, a 250 ms loop, and a continuous nudge toward the
 * room's position — with `DRIFT_PLAYING = 0.9 s` rather than 1.0 because a *highlight* on the wrong
 * sentence is instantly visible where a 0.9 s video offset is not, and no tighter than that because
 * a re-seek on Safari restarts the decoder and clicks.
 *
 * **`read`** has no clock at all. `playing` is always false, the anchor does not tick, and the 250 ms
 * loop does nothing except presence. Correction is discrete: if you are following and the room's
 * sentence is off your displayed page, the page turns; if you turn a page yourself, you stop
 * following and a pill says so. §7.4: *"movies' continuous nudge is physically wrong for silent
 * reading — re-reading a paragraph is normal and yanking someone out of it is hostile."* A reader
 * being dragged four times a minute would abandon the feature in one evening.
 *
 * ── LAW 3, and the only place two clocks meet ───────────────────────────────
 * `roomTargetMs()` below is the single point in this client where a room's clock is compared with a
 * local audio clock, and it is written the way it is on purpose. It resolves the room's **anchor**
 * from `at.sid` + `at.frac` against **this client's own cue list**, then adds the *elapsed interval*
 * since the anchor — `(predict − positionSec) × 1000`, which is wall-clock time × rate and carries no
 * axis in it at all. It never subtracts two positions on the shared `ms` axis.
 *
 * That is LAW 3 in one function. Two members can hold different `timelineVersion`s — one narrated a
 * page five minutes ago and the other did not — and their `ms` axes differ by hundreds of
 * milliseconds with nothing to indicate it. Subtracting them lands the highlight in a different
 * sentence, confidently, with no error anywhere and a listener who concludes they misread the page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BookPos } from "../../../shared/position.ts";
import { predictPosition } from "../../../shared/position.ts";
import type { PageAudio } from "../../../shared/narration/cue.ts";
import { findCue, resolveCueMs } from "../../../shared/narration/cue.ts";
import { DRIFT_PAUSED, DRIFT_PLAYING, TICK_MS } from "../../../shared/protocol.ts";

import {
  AtThrottle,
  BLOCK_DEADLINE_MS,
  RoomSocket,
  SUPPRESS_LOAD_MS,
  SUPPRESS_SEEK_MS,
  Suppressor,
  browserRoomHost,
  canControlRoom,
  isBookPos,
  memberOf,
  type ChatMessage,
  type ClientMsg,
  type ControlAction,
  type NotReady,
  type Playback,
  type RoomSettings,
  type RoomMember,
  type RoomSnapshot,
  type RoomSocketHost,
  type ServerMsg,
} from "./room.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * Constants — §7.4
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️ **The drift thresholds, the tick and the read-mode turn are `shared/protocol.ts`'s** — §2.4's
 * rule ("four documents carried four values for the same physical constant") applied to the sync
 * loop. The server enforces the same numbers; two copies of *how far apart is too far apart* is how
 * a client that thinks it is in sync gets re-seeked forever, and it is the one class of bug in this
 * subsystem with no symptom other than "it feels wrong".
 *
 * Re-exported under the local names the correction loop reads, so this file has no literals and the
 * test asserts against the shared value.
 */
export { DRIFT_PLAYING as DRIFT_PLAYING_SEC, DRIFT_PAUSED as DRIFT_PAUSED_SEC, TICK_MS, READ_TURN_MS as READ_SNAP_MS } from "../../../shared/protocol.ts";

/**
 * movies'. The element has been asked to play and has not; assume autoplay refused.
 *
 * Local, and legitimately so: it is a property of *this* client's media element and nothing the
 * server can enforce or needs to know. `GESTURE_WINDOW_MS` in the protocol is the different, shared
 * number — how long a `gesture` block may hold the ROOM (§7.5, 20 s after join).
 */
export const GESTURE_TIMEOUT_MS = 1_500;

/** How long a reaction sits on screen (§7.6: ephemeral, 3 s). Purely local rendering. */
export const REACTION_TTL_MS = 3_000;

/**
 * How long the room's anchor may stay unresolvable on this device before we call it a failure.
 *
 * ⚠️ **This is the missing half of §7.5's "wait for everyone", and it was measured missing.** The
 * block effect below is driven by `pending`, which comes from `useNarration` — and `useNarration`'s
 * `state` is about `currentId = enginePage?.audio.pageId ?? anchorPageId`, i.e. **the page on this
 * device's deck**, not the page the room is on. When the room moves onto a page this device cannot
 * get, the engine keeps the page it already holds and truthfully reports `ready` about it, so no
 * branch of the effect fires and this member says nothing at all while the room reads on without
 * them. Walked in two real Chromes — B holding chapter I with a valid cue list and no way to resolve
 * another page, while the room moved to chapter XII (`scripts/p5-gate.mjs offpage`):
 *
 *     room N9XW playing=true at c016.b0000.s0
 *     B's <audio>: {paused:true, currentTime:53.41, duration:103.22, readyState:4, error:null}
 *     in 122 s B sent NO notready at all; A's blockedBy stayed null and neither screen said anything
 *
 * B is healthy from every angle except the one that matters: socket up, seat held, presence dot
 * moving, audio element fine. It is simply reading a different chapter from everybody else.
 *
 * The signal exists in exactly one place in this client — **the correction loop's own
 * `anchorMs === null`**, which is the only code that knows "I cannot get onto the page the room is
 * on", because it is the only code that resolves the room's sentence against this device's cue list.
 * `loadUntil` already gives each load `SUPPRESS_LOAD_MS` to land; three consecutive windows with no
 * anchor is not a slow load, it is a load that is not happening.
 *
 * Deliberately generous rather than tight: the cost of being late is a few seconds of one member
 * reading behind, and the cost of being early is pausing a room for a phone that was about to
 * succeed. The block it raises carries `BLOCK_DEADLINE_MS.buffering` (60 s), so LAW 20 bounds the
 * other end whatever this number is.
 */
export const OFF_ROOM_PAGE_MS = SUPPRESS_LOAD_MS * 3;

/* ══════════════════════════════════════════════════════════════════════════
 * What the sync loop is allowed to touch
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * The audio transport, structurally. `AudioEngine` satisfies it — no adapter — and
 * `test/room-client.test.ts` asserts that assignment in the type system, the same way
 * `test/cue-sync.test.ts` pins `NarrationClock`.
 *
 * ⚠️ Deliberately **read-mostly and page-blind**. There is no `open()` here and no page id: loading a
 * page is `useNarration`'s business (it knows about `ensure`, quotas and the planner), and a sync
 * loop that could load a page would eventually load one the room is not on. When the room's anchor
 * is not on the page this transport currently holds, this file asks its caller to do it and opens a
 * suppression window; see `onLoadPage`.
 */
export interface RoomTransport {
  readonly playing: boolean;
  readonly needsGesture: boolean;
  readonly playbackRate: number;
  /**
   * The engine's own word for what it is doing. **`"loading"` is how `buffering` gets reported.**
   *
   * §7.5's table row 1 is the member whose audio stalled, `NotReady` has carried `buffering` since
   * P5, it has a 60 s deadline, the server installs it and `RoomBar` has copy for it — and no client
   * ever sent one, because the block effect could only ever produce `gesture` or `generating`. So
   * "wait for everyone" did nothing for the commonest reason to wait: one phone on weak wifi was
   * silently left a page behind while the room read on.
   */
  readonly state: "idle" | "loading" | "playing" | "paused" | "waiting" | "needs-gesture" | "error";
  readonly currentPage: { audio: PageAudio } | null;
  masterMs(): number;
  seekMasterMs(ms: number): void;
  setRate(rate: number): void;
  pause(): void;
  play(): Promise<boolean>;
}

/* ══════════════════════════════════════════════════════════════════════════
 * The correction, as a pure function
 * ══════════════════════════════════════════════════════════════════════════ */

export type Correction =
  /** Nothing to do — the common case, ~4 times a second. */
  | { do: "none" }
  /** A page swap is in flight. §7.4: the loop returns immediately while `loading`. */
  | { do: "wait" }
  | { do: "play" }
  | { do: "pause" }
  /** In-page, on **our own** cue axis. */
  | { do: "seek"; ms: number }
  /** The room is on a sentence this page does not contain. */
  | { do: "load"; at: BookPos }
  | { do: "rate"; rate: number }
  /** Autoplay was refused and there has been no gesture. Raise "Tap to join". */
  | { do: "gesture" };

export interface ListenInput {
  playback: Playback;
  /** `serverNow()` — the shared clock. */
  serverNow: number;
  /** Local wall clock, for the gesture timeout only. */
  now: number;
  /**
   * The room's anchor sentence resolved against **our** cue list, in our page's own milliseconds.
   * Null when the anchor is not on the page we hold — which is a load, not a seek.
   */
  anchorMs: number | null;
  masterMs: number;
  elPlaying: boolean;
  ready: boolean;
  loading: boolean;
  /** **Never fight a user dragging the scrubber** (§7.4, movies'). */
  scrubbing: boolean;
  needsGesture: boolean;
  rate: number;
  /** When we first noticed the element was not playing while the room was. Null when it is. */
  notPlayingSince: number | null;
}

/**
 * movies' correction loop, ported, with the page-load branch §7.4 adds.
 *
 * Pure so the test can drive it: every one of these branches is a timing decision against a clock,
 * and the only honest way to assert "a drift of 0.8 s does nothing and 1.0 s seeks" is to hand it two
 * numbers rather than to wait a second in front of a real element.
 */
export function listenCorrection(i: ListenInput): Correction {
  if (i.loading) return { do: "wait" };

  // Rate before everything else: it is not a correction, it is a *setting*, and applying it after a
  // seek means the seek was computed for the wrong playback speed for one tick.
  if (i.playback.rate !== i.rate) return { do: "rate", rate: i.playback.rate };

  // §7.4, movies': **never fight a user dragging the scrubber.** Above the load and not below it —
  // a load is a page swap, which is the most violent thing that could happen under a moving thumb.
  if (i.scrubbing) return { do: "none" };

  /**
   * ⚠️ **The load is decided BEFORE `ready`, and the other order is a silent deadlock.**
   *
   * movies' loop opens with *"bail if the player is not ready"*, and porting that literally put
   * `if (!i.ready) return {do:"none"}` first. Here `ready` is bound to `page !== null` — *this client
   * holds an artifact* — so on a cold join the engine holds nothing, `ready` is false, the loop
   * returns `none` on every one of its four ticks a second, and `{do:"load"}` is **unreachable**: the
   * only branch that could ever give this client a page was gated on already having one.
   *
   * Measured in two real browsers: the follower received `{t:"sync"} playing:true` and sat with
   * `<audio readyState=0 src="">` forever, no request, no error, its own bar reading "Play" over a
   * page whose 99.5 s of audio was on disk. The room played on the device that had pressed the
   * button and was silent on every other device — which is the entire feature.
   *
   * `anchorMs === null` already covers both cases (`roomAnchorMs` returns null for a null page *and*
   * for a page that does not contain the room's sentence), and the load is idempotent behind
   * `loadUntil`'s deadline, so this is the whole fix. `ready` keeps its job below: guarding the drift
   * arithmetic, which is meaningless without a cue list.
   */
  if (i.anchorMs === null) return { do: "load", at: i.playback.at };

  if (!i.ready) return { do: "none" };

  const elapsedMs = (predictPosition(i.playback, i.serverNow) - i.playback.positionSec) * 1000;
  const target = i.anchorMs + elapsedMs;

  if (i.playback.playing) {
    if (!i.elPlaying) {
      /**
       * ⚠️ **`needsGesture` must SAY SO, not go quiet.** This branch used to `return {do:"none"}`,
       * on the reading that we already know a gesture is needed so there is no point asking the
       * element again. That reading is right about the element and wrong about the screen: the input
       * is `gestureRef.current || transport.needsGesture`, and the second term is the *engine's* —
       * true from module load until `unlock()` runs inside a real transient activation. Nothing in
       * the loop ever turned that into the hook's own `needsGesture` state, so `<TapToJoin>` was
       * unreachable in precisely the situation it exists for.
       *
       * Measured in two real Chromes: the follower loaded the artifact (`readyState=4`,
       * `duration=99.5`), seeked to the room's sentence (`currentTime=0.79`), lit the right sentence
       * — and stayed `paused` for ever, with no overlay, no prompt and nothing in any console. The
       * room played on one device and was a still page on the other.
       *
       * `setGesture(true)` is idempotent, so re-deciding this every tick costs a React no-op.
       */
      if (i.needsGesture) return { do: "gesture" };
      // movies': ask, and if it has still not started after 1500 ms, conclude autoplay refused. A
      // rejected `play()` promise is the other signal and the caller wires both — Safari resolves
      // the promise and simply does not play often enough that the timeout is the reliable half.
      if (i.notPlayingSince !== null && i.now - i.notPlayingSince > GESTURE_TIMEOUT_MS) {
        return { do: "gesture" };
      }
      return { do: "play" };
    }
    return Math.abs(i.masterMs - target) > DRIFT_PLAYING * 1000 ? { do: "seek", ms: target } : { do: "none" };
  }

  if (i.elPlaying) return { do: "pause" };
  return Math.abs(i.masterMs - target) > DRIFT_PAUSED * 1000 ? { do: "seek", ms: target } : { do: "none" };
}

/**
 * §7.4's `read` policy, entire. **Snap on `sid` change only.**
 *
 * There is no clock, no tolerance and no continuous term — those are the three things that make
 * `listen` correct and `read` hostile. The only question is whether the room's sentence is on the
 * page in front of this reader, and the only actions are "turn there" and "do nothing".
 */
export function readCorrection(i: {
  roomSid: string | null;
  following: boolean;
  /** Is the room's sentence rendered on the display page this reader is looking at? */
  onMyPage: boolean;
  /** The last sid we snapped to, so a page turn *back* by the reader is not undone next tick. */
  lastSnappedSid: string | null;
}): { snap: true; sid: string } | { snap: false } {
  if (!i.following || i.roomSid === null) return { snap: false };
  if (i.onMyPage) return { snap: false };
  // Already snapped there and the reader has since turned away: that turn cleared `following`, and
  // if it did not, this is a reader who moved inside the same room sentence's page. Either way,
  // snapping again would be the yank §7.4 forbids.
  if (i.lastSnappedSid === i.roomSid) return { snap: false };
  return { snap: true, sid: i.roomSid };
}

/**
 * Resolve the room's anchor against **this client's** cue list. LAW 3, and the reason `frac` exists.
 *
 * Returns null when the sentence is not on this page — which is a page load and not a seek, and
 * conflating the two is how a client seeks to a millisecond that means something else on the artifact
 * it is actually holding.
 */
export function roomAnchorMs(page: PageAudio | null | undefined, at: BookPos | null | undefined): number | null {
  if (!page || !at) return null;
  const cue = findCue(page.cues, at.sid);
  if (cue === undefined) return null;
  return resolveCueMs(cue, at.frac);
}

/* ══════════════════════════════════════════════════════════════════════════
 * useRoom — the socket, as state
 * ══════════════════════════════════════════════════════════════════════════ */

export interface FloatingReaction {
  id: number;
  emoji: string;
  name: string;
  /** §7.6: reactions are `{emoji, sid}`. **There is no xy.** */
  sid: string | null;
}

export type RoomError =
  | null
  | "notfound"
  | "locked"
  | "throttled"
  | "kicked"
  | "ended"
  | "access-revoked"
  | "taken-down"
  | "other";

export interface RoomApi {
  connected: boolean;
  room: RoomSnapshot | null;
  playback: Playback | null;
  chat: ChatMessage[];
  reactions: FloatingReaction[];
  /**
   * §7.6's *"Resume — Genesis 4, where you stopped on Tuesday."* Null unless the server offered it.
   *
   * `personal` is this member's **own** `positions` row, carried beside the room's. They are
   * different rows and the app asks once — *"Resume together (ch. 12, 3 days ago) or where you left
   * off (ch. 14)?"* Merging them automatically silently destroys somebody's progress, so the server
   * sends both and decides neither, and neither does this hook.
   */
  resumeOffer: { at: BookPos; since: number; personal: BookPos | null } | null;
  /**
   * §7.5's honest state, for one member.
   *
   * **This is how LAW 20 speaks.** When a block hits its deadline the room resumes without that
   * member, and a room that simply started moving again with no explanation is the most alarming
   * version of the feature. The flash is the sentence — *"the voice service is offline — you're
   * reading ahead of the narration"* — and dropping it on the floor would make the deadline look
   * like a glitch.
   */
  flash: { seq: number; text: string; kind: "good" | "bad" | "info" } | null;
  error: RoomError;
  errorText: string | null;
  /** Our own `actorKey`, as the server names us. Null before `welcome`. */
  myKey: string | null;
  me: RoomMember | null;
  canControl: boolean;
  serverNow(): number;

  createRoom(opts: Omit<Extract<ClientMsg, { t: "create" }>, "t">): void;
  /**
   * §7.6 + §6.5: close the room for **everybody**. Host only; the server re-checks.
   *
   * Distinct from `leave`, which gives up one seat. Without it `RoomGone`'s *"Whoever started it
   * closed it"* described nothing, and §6.5's quota — which counts rooms that have not ended, over
   * persistent rooms that are never garbage-collected — had no way down.
   */
  endRoom(): void;
  joinRoom(code: string, password?: string): void;
  leave(): void;
  control(action: ControlAction): void;
  sendAt(pos: BookPos, following: boolean, visibleEndSid?: string | null): void;
  setFollowing(on: boolean): void;
  reportNotReady(reason: NotReady | null, opts?: { pageId?: string; etaSec?: number }): void;
  sendChat(text: string): void;
  sendReaction(emoji: string, sid?: string): void;
  /** `RoomSettings` from `shared/protocol.ts` — restating the eight fields here is how the two ends
   *  end up disagreeing about which of them is optional. */
  updateSettings(s: RoomSettings): void;
  passRemote(key: string): void;
  addHost(key: string): void;
  removeHost(key: string): void;
  kick(key: string): void;
  dismissResume(): void;
  dismissFlash(): void;
  clearError(): void;
  /** True for one render after a control was refused, so the UI can flash rather than nag. */
  denied: number;
  /** Bumped on every reconnect that re-joined. `useRoomSync` resyncs on it (§7.5). */
  rejoinNonce: number;
}

/**
 * One socket per mounted room screen.
 *
 * `host` is injectable for the same reason everything else in this repo is: `node --test` has no
 * `WebSocket`. `browserRoomHost()` returns null there, and this hook then returns a fully-formed API
 * whose actions are no-ops — which is also the correct behaviour in a browser with no room.
 */
export function useRoom(opts: { host?: RoomSocketHost | null; name?: string | null } = {}): RoomApi {
  const [connected, setConnected] = useState(false);
  const [room, setRoom] = useState<RoomSnapshot | null>(null);
  const [playback, setPlayback] = useState<Playback | null>(null);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [reactions, setReactions] = useState<FloatingReaction[]>([]);
  const [resumeOffer, setResumeOffer] = useState<RoomApi["resumeOffer"]>(null);
  const [flash, setFlash] = useState<RoomApi["flash"]>(null);
  const flashSeq = useRef(0);
  const [error, setError] = useState<RoomError>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [myKey, setMyKey] = useState<string | null>(null);
  const [denied, setDenied] = useState(0);
  const [rejoinNonce, setRejoinNonce] = useState(0);

  const reactionSeq = useRef(0);
  const reactionTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const sockRef = useRef<RoomSocket | null>(null);

  // The host is resolved once. `browserRoomHost()` reads `location`, and a hook that re-resolved it
  // every render would re-create the socket on every state change — i.e. four times a second.
  const [host] = useState<RoomSocketHost | null>(() => opts.host ?? browserRoomHost());

  const onMessage = useCallback((msg: ServerMsg) => {
    switch (msg.t) {
      case "welcome":
        setMyKey(msg.actorKey);
        setError(null);
        setErrorText(null);
        break;
      case "room":
        setRoom(msg.room);
        setPlayback(msg.room.playback);
        break;
      case "sync":
        setPlayback(msg.playback);
        break;
      case "at":
        // The hot path. Patch the one member rather than replacing the snapshot: at 2/s per member a
        // full `setRoom` would re-render every consumer of the room object — the member list, the
        // chat, the bar — for a dot moving four millimetres.
        setRoom((r) => {
          if (r === null) return r;
          const i = r.members.findIndex((m) => m.key === msg.key);
          if (i < 0) return r;
          const members = r.members.slice();
          members[i] = { ...members[i]!, at: msg.pos, following: msg.following };
          return { ...r, members };
        });
        break;
      case "chatHistory":
        setChat(msg.msgs);
        break;
      case "chat":
        // 300 is movies'. A persistent room's chat is durable on the server (§7.6, `room_chat`); this
        // is the window kept in memory, and an unbounded array on a room that runs for a year is a
        // leak that only shows up on the device least able to absorb it.
        setChat((c) => [...c, msg.msg].slice(-300));
        break;
      case "reaction": {
        const id = ++reactionSeq.current;
        setReactions((r) => [...r, { id, emoji: msg.emoji, name: msg.name, sid: msg.sid ?? null }].slice(-24));
        const t = setTimeout(() => {
          reactionTimers.current.delete(t);
          setReactions((r) => r.filter((x) => x.id !== id));
        }, REACTION_TTL_MS);
        reactionTimers.current.add(t);
        break;
      }
      case "resume":
        setResumeOffer({ at: msg.at, since: msg.since, personal: msg.personal });
        break;
      case "flash":
        // A sequence rather than a timer here: the *screen* decides how long a notice lives, and a
        // hook that expired it on its own would race a component that had not rendered it yet.
        setFlash({ seq: ++flashSeq.current, text: msg.text, kind: msg.kind ?? "info" });
        break;
      case "timeline":
        // §7.3: a room pins `timelineVersion` at open and migrates only at a page boundary or an
        // explicit resync — **by `sid` → `ms`, never `ms` → `sid`**. The server has done the
        // migration and is telling us the new pin and where the room now is on it; the local audio
        // clock is re-anchored on the next tick from `at.sid`, which is the only direction that is
        // safe. There is nothing for the client to compute here, and that is the point.
        setPlayback((p) => (p === null ? p : { ...p, timelineVersion: msg.version, at: msg.at }));
        break;
      case "ended":
        // Each reason the server can name maps to its own screen. Anything else — including
        // `host-ended` and a reason-less close — is "the room ended", which is the honest default.
        // `kicked` in particular must NOT fall through: the room is still running, and telling
        // somebody it closed sends them to reload the page and straight back into the refusal.
        setError(
          msg.reason === "access-revoked" || msg.reason === "taken-down" || msg.reason === "kicked"
            ? msg.reason
            : "ended",
        );
        setRoom(null);
        setPlayback(null);
        break;
      case "error":
        switch (msg.message) {
          case "room-locked":
          case "locked":
            // Keep the socket open: the join is retried the moment a password is typed.
            setError("locked");
            break;
          case "too-many-attempts":
            setError("throttled");
            break;
          case "not-found":
          case "Room not found":
            setError("notfound");
            break;
          case "kicked":
            setError("kicked");
            setRoom(null);
            break;
          case "denied":
          case "not-host":
            // Not an error state — a refused control. The correction loop is already putting this
            // member back where the room is; all the UI owes them is a flash.
            setDenied((n) => n + 1);
            break;
          default:
            setError("other");
            setErrorText(msg.message);
            break;
        }
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (host === null) return;
    const sock = new RoomSocket(host, {
      message: onMessage,
      connection: (up) => setConnected(up),
      rejoined: () => setRejoinNonce((n) => n + 1),
    });
    sockRef.current = sock;
    return () => {
      sockRef.current = null;
      sock.destroy();
      for (const t of reactionTimers.current) clearTimeout(t);
      reactionTimers.current.clear();
    };
  }, [host, onMessage]);

  useEffect(() => {
    sockRef.current?.setName(opts.name ?? null);
  }, [opts.name]);

  const serverNow = useCallback(() => sockRef.current?.serverNow() ?? Date.now(), []);
  const send = useCallback((m: ClientMsg) => sockRef.current?.send(m), []);

  const me = useMemo(() => memberOf(room, myKey), [room, myKey]);
  const canControl = useMemo(() => canControlRoom(room, myKey), [room, myKey]);

  return {
    connected,
    room,
    playback,
    chat,
    reactions,
    resumeOffer,
    flash,
    error,
    errorText,
    myKey,
    me,
    canControl,
    serverNow,
    denied,
    rejoinNonce,
    createRoom: useCallback((o) => sockRef.current?.create(o), []),
    joinRoom: useCallback((code, password) => sockRef.current?.join(code, password), []),
    leave: useCallback(() => {
      sockRef.current?.leave();
      setRoom(null);
      setPlayback(null);
      setChat([]);
      setReactions([]);
      setConnected(false);
    }, []),
    /**
     * Close the room for everybody. Host only — the server re-checks, this is the courtesy half.
     *
     * Local state is NOT cleared here, unlike `leave`: the server answers with `{t:"ended"}`, which
     * the message handler turns into `error: "ended"` and the `<RoomGone>` screen. Clearing it here
     * as well would race that frame and show the blank state instead of the sentence that explains
     * what happened.
     */
    endRoom: useCallback(() => send({ t: "end" }), [send]),
    control: useCallback((action) => send({ t: "control", action }), [send]),
    sendAt: useCallback(
      (pos, following, visibleEndSid) =>
        send({ t: "at", pos, following, ...(visibleEndSid ? { visibleEndSid } : {}) }),
      [send],
    ),
    setFollowing: useCallback((on) => send({ t: "follow", on }), [send]),
    reportNotReady: useCallback(
      (reason, o) => send({ t: "notready", reason, pageId: o?.pageId, etaSec: o?.etaSec }),
      [send],
    ),
    sendChat: useCallback(
      (text) => {
        const t = text.trim();
        if (t) send({ t: "chat", text: t });
      },
      [send],
    ),
    sendReaction: useCallback((emoji, sid) => send({ t: "reaction", emoji, sid }), [send]),
    updateSettings: useCallback((s) => send({ t: "settings", ...s }), [send]),
    passRemote: useCallback((key) => send({ t: "passRemote", key }), [send]),
    addHost: useCallback((key) => send({ t: "addHost", key }), [send]),
    removeHost: useCallback((key) => send({ t: "removeHost", key }), [send]),
    kick: useCallback((key) => send({ t: "kick", key }), [send]),
    dismissResume: useCallback(() => setResumeOffer(null), []),
    dismissFlash: useCallback(() => setFlash(null), []),
    clearError: useCallback(() => {
      setError(null);
      setErrorText(null);
    }, []),
  };
}

/* ══════════════════════════════════════════════════════════════════════════
 * useRoomSync — the loop
 * ══════════════════════════════════════════════════════════════════════════ */

export interface RoomSyncOptions {
  room: RoomSnapshot | null;
  playback: Playback | null;
  serverNow: () => number;
  myKey: string | null;
  canControl: boolean;
  control: (action: ControlAction) => void;
  /**
   * `{t:"at"}`. **Not a control** — presence is never refused and never goes through `controlMode`,
   * so it is a separate seam from `control` rather than another `ControlAction`. A room where a
   * member without the remote also stops appearing on the scrubber is a room that has confused
   * "may drive" with "is here".
   */
  sendAt: (pos: BookPos, following: boolean, visibleEndSid?: string | null) => void;
  reportNotReady: (reason: NotReady | null, opts?: { pageId?: string; etaSec?: number }) => void;
  rejoinNonce: number;

  /** The engine. Null on a book with no narration, or in `read` mode, or under `node --test`. */
  transport: RoomTransport | null;
  /** True while the narration plug is fetching/opening a page. Gates the loop (§7.4). */
  loading: boolean;
  /** True while the reader has a finger on the scrubber. */
  scrubbing: boolean;
  /**
   * Load the page containing this position and seek into it. Supplied by whoever owns
   * `useNarration` — this file deliberately cannot open a page. Returning is what ends the load
   * window, so it must not resolve before the swap has happened.
   */
  onLoadPage: (at: BookPos) => Promise<void> | void;

  /** `read` mode only. Is the room's sentence rendered on the page in front of this reader? */
  onMyPage?: ((sid: string) => boolean) | null;
  /** `read` mode only. Move the reader to this sentence. */
  onSnap?: ((sid: string) => void) | null;
  /** LAW 12's flag for this member, owned by the reader. */
  following: boolean;

  /** Our own place in the book, for `{t:"at"}`. Null until the reader has landed a page. */
  myPos: BookPos | null;
  /** Last sentence visible on this device's current page/spread, when layout has measured it. */
  visibleEndSid?: string | null;
  /**
   * What this member is waiting on, for §7.5's blocks. Three facts about **this device**, never a
   * claim about the room — the server decides what any of them mean.
   */
  pending?: {
    /** The planner's answer for `pageId`. */
    state: string;
    etaSec: number | null;
    pageId: string | null;
    /**
     * The engine ran out of page. `AudioEngine.state === "waiting"`, i.e. the deck finished and the
     * next artifact is not there — true whether the page is queued, building, refused, or the voice
     * service is down, which is exactly why it and not `state` decides the `generating` report.
     */
    waiting?: boolean;
    /** The engine is loading and is not playing: §7.5's `buffering`. */
    stalled?: boolean;
  } | null;
}

export interface RoomSyncApi {
  /** §7.5's `gesture`: autoplay was refused. The "Tap to join" overlay is rendered on this. */
  needsGesture: boolean;
  /** A real gesture. Unlocks and puts us back on the room clock. */
  tapToJoin: () => void;
  /**
   * §7.1's `resync()` — **local only, emits no intent.** "Put me back where they are."
   *
   * Even more useful for reading than for a film, where drifting apart intentionally is normal: it
   * is the button a person presses after flipping back three pages to check a name.
   */
  resync: () => void;
  /** `read` mode: the sentence the reader should be showing, once it has been decided. */
  snapSid: string | null;
  /** What this member is currently reporting as their block, for the bar's own copy. */
  blocking: NotReady | null;
  /** The room's own view: who is holding everyone up, if anyone. */
  blockedBy: Playback["blockedBy"];
  /** Intent helpers that respect `controlMode` — a refused press must not act locally either. */
  intendPlayPause: (at: BookPos | null) => void;
  intendSeek: (at: BookPos) => void;
  intendRate: (rate: number) => void;
  /**
   * §7.2's *"next page/chapter — routine operation"*, and **LAW 19's non-destructive half**.
   *
   * ⚠️ **Nothing in the client emitted this for the whole of P5, and that made a listening room break
   * at its first page boundary.** `ControlAction.advance` was defined in `shared/protocol.ts`, handled
   * in `rooms/ws.ts` with its `behind` gate and its `repinIfMoved`, and driven by tests that called
   * `room.applyControl` directly. No browser code path ever constructed one.
   *
   * The consequence is not "the room's bookmark lags". It is a loop. `rooms.at` stays pinned on the
   * sentence play was pressed on; the `AudioEngine` reaches the end of that page and advances itself
   * on **every** device; the next 250 ms tick calls `roomAnchorMs(newPage, at)`, `findCue` misses,
   * the anchor is null, and `listenCorrection` returns `{do:"load"}` — which pulls every reader
   * *back* to the page play started on. The anchor then resolves, `elapsedMs` is past that page's
   * end, `seekMasterMs` clamps to the end, `nearEnd` fires `advance()` again, and the room oscillates
   * at the first boundary and can never play a chapter through. P5's gate never saw it because it
   * never crossed a boundary inside a room: its longest clause ran 118 s on one 99.5 s page.
   *
   * Two more things came out of the same root. §7.5's prefetch window is anchored on `rooms.sid`, so
   * *"six pages ahead of the room"* meant six pages ahead of wherever play was pressed, for ever. And
   * §7.6's resume offered the sentence play was *pressed* on rather than where the evening stopped —
   * P5's night walk recorded `c010.b0000.s0`, the first sentence of the chapter, after 118 s of
   * audio, and passed.
   */
  intendAdvance: (at: BookPos) => void;
}

export function useRoomSync(opts: RoomSyncOptions): RoomSyncApi {
  const [needsGesture, setNeedsGesture] = useState(false);
  const [snapSid, setSnapSid] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<NotReady | null>(null);
  /**
   * "The room's sentence has not been on my page for `OFF_ROOM_PAGE_MS`, and it is not for want of
   * asking." Written by the correction loop, read by the block effect — see `OFF_ROOM_PAGE_MS`.
   */
  const [offRoomPage, setOffRoomPage] = useState(false);
  const offPageSince = useRef<number | null>(null);

  /**
   * Everything the loop reads lives in a ref written during render.
   *
   * The loop is a single `setInterval` armed once for the life of the room. Putting `playback` in its
   * dependency array would tear the interval down and rebuild it on every `sync` frame — which on a
   * moving room is several times a second — and each rebuild resets the phase, so the 250 ms tick
   * would run at an interval nobody chose. `Reader.tsx` uses the same `live` ref for the same reason.
   */
  const live = useRef(opts);
  live.current = opts;

  const suppress = useRef(new Suppressor());
  const throttle = useRef(new AtThrottle());
  const notPlayingSince = useRef<number | null>(null);
  const lastSnapped = useRef<string | null>(null);
  /**
   * §7.4's page-load window, **as a deadline rather than as a promise**.
   *
   * The obvious implementation — a boolean held for the duration of an `await` — does not work here,
   * and the way it fails is the exact failure §7.4 describes. The call that changes the page
   * (`useNarration.onPlayFrom`) is fire-and-forget: it returns before the `ensure` round-trip, before
   * the artifact resolves and long before `loadedmetadata`. A boolean released on that return is
   * released 200 ms early, the next tick still sees the old page and no anchor on it, issues a second
   * load, and **the two race forever** — "this is the audiobook version of movies' scrubbing guard".
   *
   * So the latch is a timestamp: set to `now + SUPPRESS_LOAD_MS` when a load is issued, and cleared
   * *early* the moment the room's anchor actually resolves against the page we hold, which is the
   * only observation that means the load landed. Slow loads are covered by the deadline; fast ones
   * cost nothing.
   */
  const loadUntil = useRef(0);
  const joinedAt = useRef<number>(Date.now());
  const reportedRef = useRef<NotReady | null>(null);
  const gestureRef = useRef(false);

  const setGesture = useCallback((v: boolean) => {
    gestureRef.current = v;
    setNeedsGesture(v);
  }, []);

  /** The room we are in. A change of code is a change of room and every latch has to reset. */
  const code = opts.room?.code ?? null;
  useEffect(() => {
    joinedAt.current = Date.now();
    lastSnapped.current = null;
    notPlayingSince.current = null;
    reportedRef.current = null;
    loadUntil.current = 0;
    offPageSince.current = null;
    throttle.current.reset();
    suppress.current.clear();
    setGesture(false);
    setSnapSid(null);
    setOffRoomPage(false);
  }, [code, setGesture]);

  /* ── the block this member reports about itself (§7.5) ─────────────────── */

  useEffect(() => {
    const o = live.current;
    let want: NotReady | null = null;

    /**
     * ⚠️ **"The room is reading" is `playing || waitingForEveryone`, and `playing` alone is a flap.**
     *
     * `recomputePlaying()` sets `playing = intendedPlaying && !blocked` and
     * `waitingForEveryone = intendedPlaying && blocked`. So the instant the server acts on a report
     * from this member, the very field the report was gated on goes false — the effect re-runs on the
     * next `sync`, computes `want = null`, sends `{t:"notready",reason:null}`, the server clears the
     * block and sets `playing` back to true, and the whole thing repeats several times a second. The
     * room never actually waits and the banner strobes.
     *
     * `waitingForEveryone` is the room saying *somebody asked for this to be playing and something is
     * holding it*, which is the condition these reports are actually about. LAW 20 still bounds it
     * from the other end: `setBlock` does not extend a deadline on a repeated report, so a member who
     * keeps saying the same thing is dropped on schedule rather than holding the evening.
     */
    const reading = o.playback?.playing === true || o.playback?.waitingForEveryone === true;

    if (gestureRef.current) {
      // §7.5: `gesture` blocks **for 20 s after join only**. A member who walked away from a phone
      // with the tab open must not hold a Tuesday room frozen behind a banner all evening, and the
      // deadline is measured from the join because that is the moment the tap was plausible.
      const deadline = BLOCK_DEADLINE_MS.gesture ?? 0;
      if (Date.now() - joinedAt.current < deadline) want = "gesture";
    } else if (
      reading &&
      o.pending?.waiting !== undefined &&
      (o.pending.waiting === true || (o.pending.pageId !== null && o.pending.state !== "ready"))
    ) {
      /**
       * ⚠️ **The engine has run out of page and stopped. That is the report, whatever the plan says.**
       *
       * This used to read `state === "queued" || state === "building"`, and the gap it left is the
       * one case the room most needs to explain. `ensure` **fails fast** when spark-1 is unreachable
       * — it does not enqueue, so nothing is ever `queued` — and `useNarration` records the plan's
       * answer as `missing`. Neither string matched, so no report was sent, so the server was never
       * asked, so `ws.ts`'s LAW 20 refusal branch — which is built, tested, and exists precisely to
       * say *"the voice service is offline, you're reading ahead of the narration"* — never ran.
       * Measured with the GPU genuinely down: 0 blocks and 0 jobs (both correct) and **0 flashes**
       * (not). The room simply sat there.
       *
       * The condition is **"the room is reading and I cannot"**, in two shapes, and the first pass at
       * this fix only had one of them:
       *
       *   · `waiting` — `AudioEngine.state === "waiting"`: the deck finished a page and the next one
       *     is not there. This is the common case (page N ends, page N+1 is cold).
       *   · the room is playing and the page this member is on is **not `ready`** — which is what
       *     happens when somebody opens a cold chapter and presses play. Measured with the GPU down:
       *     the reader sat on `c009.b0000.s0` with `data-narration-state="failed"`, the engine was
       *     `idle` rather than `waiting` (nothing had ever played), so a `waiting`-only report sent
       *     **nothing** and the room again said nothing. Exactly the defect, one case over.
       *
       * ⚠️ **Both are gated on `playback.playing`, and that gate is LAW 12.** Turning a page is
       * browsing and browsing is private: a member who wanders onto an un-narrated page while the
       * room is *paused* must not report anything, or reading ahead would hold everybody. It is only
       * once the room is actually reading that "I cannot follow" is the room's business.
       *
       * Every term is a fact about **this device**, which is the only thing a client may assert. The
       * server still decides what it means — `admitGeneratingBlock` refuses a page that is already
       * `ready`, refuses one that cannot be planned, and refuses one whose plan is blocked, sending
       * `verdict.why` instead of installing anything.
       */
      want = "generating";
    } else if (o.pending && (o.pending.state === "queued" || o.pending.state === "building")) {
      want = "generating";
    } else if (reading && (o.pending?.stalled === true || offRoomPage)) {
      /**
       * §7.5's first table row, finally reachable: *the room is playing and my audio is not, and it
       * is not because I owe it a tap.* `buffering` carries a 60 s deadline (`BLOCK_DEADLINE_MS`), so
       * a phone that never recovers costs the room a minute and not the evening — which is LAW 20's
       * whole shape and the reason this is safe to report at all.
       *
       * ⚠️ **`offRoomPage` is the case `pending` structurally cannot see** — the member who cannot
       * load the room's page *at all*. See `OFF_ROOM_PAGE_MS`: `pending` describes the page on this
       * device's deck, and a device that never got onto the room's page is still holding an older one
       * and still calling it `ready`. Measured saying nothing for 122 s while the room read on.
       *
       * ⚠️ **It reports `buffering` and not `generating`, and that is deliberate rather than a
       * shortcut.** `ws.ts`'s handler opens `if (!pageId) return;` — it must `planPage()` a page
       * before it may believe a `generating` claim — and this loop **has no page id and must not
       * acquire one**: it is page-blind on purpose (see `RoomTransport`, "a sync loop that could load
       * a page would eventually load one the room is not on"). `buffering` needs no page, its copy is
       * exactly what is true — *"Waiting for X — their audio is still loading"* — and its 60 s
       * deadline is shorter than `generating`'s 120 s, which is the right way round for a claim this
       * one cannot substantiate with a plan.
       */
      want = "buffering";
    }

    if (want === reportedRef.current) return;
    reportedRef.current = want;
    setBlocking(want);
    o.reportNotReady(
      want,
      want === "generating"
        ? { pageId: o.pending?.pageId ?? undefined, etaSec: o.pending?.etaSec ?? undefined }
        : undefined,
    );
  }, [
    needsGesture,
    offRoomPage,
    opts.pending?.state,
    opts.pending?.pageId,
    opts.pending?.etaSec,
    opts.pending?.waiting,
    opts.pending?.stalled,
    opts.playback?.playing,
    opts.playback?.waitingForEveryone,
  ]);

  /* ── the load window ───────────────────────────────────────────────────── */

  const loadPage = useCallback((at: BookPos) => {
    const now = Date.now();
    if (now < loadUntil.current) return;
    loadUntil.current = now + SUPPRESS_LOAD_MS;
    // Opened BEFORE the load, not after: a page swap fires `pause`, `emptied`, `loadedmetadata`,
    // `seeked` and `play` over the following few hundred milliseconds, and the window has to be
    // standing when the first of them arrives. Monotonic-max, so a seek that lands mid-load extends
    // it (§7.4) rather than cutting it short.
    suppress.current.open(SUPPRESS_LOAD_MS, now);
    try {
      const r = live.current.onLoadPage(at);
      // A page that will not load is the `generating`/`buffering` story, reported above and given a
      // deadline by LAW 20. Swallowing it here keeps the loop alive; an unhandled rejection out of a
      // `setInterval` callback would not.
      if (r && typeof (r as Promise<void>).catch === "function") void (r as Promise<void>).catch(() => undefined);
    } catch {
      /* as above */
    }
  }, []);

  /* ── the 250 ms loop ───────────────────────────────────────────────────── */

  useEffect(() => {
    const iv = setInterval(() => {
      const o = live.current;
      const now = Date.now();

      /**
       * Presence runs in BOTH modes and is the only thing `read` mode's tick does (§7.4).
       *
       * It is here rather than in an effect on `myPos` because the throttle has a trailing edge: the
       * last position of a burst has to be flushed by *something*, and an effect that only fires on
       * change cannot flush a value that has stopped changing. Without it a member's dot parks one
       * sentence behind where they actually stopped until they move again.
       */
      const at =
        o.myPos !== null
          ? (throttle.current.offer(o.myPos, o.following, now, o.visibleEndSid) ?? throttle.current.drain(now))
          : throttle.current.drain(now);
      if (at !== null) o.sendAt(at.pos, at.following, at.visibleEndSid);

      const pb = o.playback;
      if (pb === null) return;

      if (pb.mode === "read") {
        const decision = readCorrection({
          roomSid: pb.at?.sid ?? null,
          following: o.following,
          onMyPage: pb.at ? (o.onMyPage?.(pb.at.sid) ?? false) : true,
          lastSnappedSid: lastSnapped.current,
        });
        if (decision.snap) {
          lastSnapped.current = decision.sid;
          setSnapSid(decision.sid);
          o.onSnap?.(decision.sid);
        }
        return;
      }

      const t = o.transport;
      if (t === null) return;

      const page = t.currentPage?.audio ?? null;
      const anchorMs = roomAnchorMs(page, pb.at);
      // The load landed — the room's sentence is on the page we hold. Release the latch early rather
      // than sitting out the rest of a 1800 ms deadline we no longer need.
      if (anchorMs !== null) loadUntil.current = 0;

      /**
       * §7.5's missing report, measured here and nowhere else. See `OFF_ROOM_PAGE_MS`.
       *
       * ⚠️ **Cleared whenever the room is not trying to read, and that clause is LAW 12.** Browsing is
       * private: a member who wanders onto a page the room is not on, while the room is paused, must
       * not hold anybody. The condition is only the room's business once the room is actually reading,
       * which is the same gate every other report in this hook is behind.
       */
      const reading = pb.playing || pb.waitingForEveryone;
      if (anchorMs !== null || !reading) {
        offPageSince.current = null;
        setOffRoomPage(false);
      } else {
        offPageSince.current ??= now;
        if (now - offPageSince.current > OFF_ROOM_PAGE_MS) setOffRoomPage(true);
      }

      const correction = listenCorrection({
        playback: pb,
        serverNow: o.serverNow(),
        now,
        anchorMs,
        masterMs: t.masterMs(),
        elPlaying: t.playing,
        ready: page !== null,
        loading: o.loading || now < loadUntil.current,
        scrubbing: o.scrubbing,
        needsGesture: gestureRef.current || t.needsGesture,
        rate: t.playbackRate,
        notPlayingSince: notPlayingSince.current,
      });

      switch (correction.do) {
        case "none":
        case "wait":
          if (correction.do === "none" && t.playing) notPlayingSince.current = null;
          if (correction.do === "none" && gestureRef.current && t.playing) setGesture(false);
          break;
        case "rate":
          suppress.current.open(SUPPRESS_SEEK_MS, now);
          t.setRate(correction.rate);
          break;
        case "play":
          if (notPlayingSince.current === null) notPlayingSince.current = now;
          suppress.current.open(SUPPRESS_SEEK_MS, now);
          void t.play().then((ok) => {
            if (!ok) setGesture(true);
          });
          break;
        case "pause":
          notPlayingSince.current = null;
          suppress.current.open(SUPPRESS_SEEK_MS, now);
          t.pause();
          break;
        case "seek":
          suppress.current.open(SUPPRESS_SEEK_MS, now);
          t.seekMasterMs(correction.ms);
          break;
        case "load":
          loadPage(correction.at);
          break;
        case "gesture":
          setGesture(true);
          break;
      }
    }, TICK_MS);
    return () => clearInterval(iv);
  }, [loadPage, setGesture]);

  /* ── resync ────────────────────────────────────────────────────────────── */

  const resync = useCallback(() => {
    const o = live.current;
    const pb = o.playback;
    if (pb === null) return;
    if (pb.mode === "read") {
      if (pb.at) {
        lastSnapped.current = pb.at.sid;
        setSnapSid(pb.at.sid);
        o.onSnap?.(pb.at.sid);
      }
      return;
    }
    const t = o.transport;
    if (t === null) return;
    const page = t.currentPage?.audio ?? null;
    const anchor = roomAnchorMs(page, pb.at);
    const now = Date.now();
    suppress.current.open(SUPPRESS_SEEK_MS, now);
    if (anchor === null) {
      loadPage(pb.at);
      return;
    }
    t.seekMasterMs(anchor + (predictPosition(pb, o.serverNow()) - pb.positionSec) * 1000);
    if (pb.playing) {
      void t.play().then((ok) => {
        if (!ok) setGesture(true);
      });
    }
    notPlayingSince.current = null;
  }, [loadPage, setGesture]);

  /**
   * §7.5: *"On reconnect, `resync()`."*
   *
   * An iPhone suspends a backgrounded tab's WebSocket within ~30 s of screen lock while the audio
   * keeps playing, so the room moves on without us on every single lock. The socket rejoins on its
   * own; this is the other half — put the audio back where the room is, **without emitting an
   * intent**, because a reconnecting phone must not drag everybody back to where it happened to be.
   */
  useEffect(() => {
    if (opts.rejoinNonce === 0) return;
    resync();
  }, [opts.rejoinNonce, resync]);

  const tapToJoin = useCallback(() => {
    setGesture(false);
    notPlayingSince.current = null;
    resync();
  }, [resync, setGesture]);

  /* ── intents ───────────────────────────────────────────────────────────── */

  /**
   * A press that the room may refuse.
   *
   * ⚠️ **A refused press must not act locally either.** movies' comment is the same one: the
   * correction loop will snap them back to the shared state, so a local play that the room then
   * cancels is a quarter-second of audio and a member wondering what they did wrong. The honest
   * shape is that the control does nothing and the UI says who has the remote.
   */
  const intendPlayPause = useCallback((at: BookPos | null) => {
    const o = live.current;
    const pb = o.playback;
    if (pb === null || !o.canControl) return;
    const target = at ?? pb.at;
    suppress.current.open(SUPPRESS_SEEK_MS, Date.now());
    o.control({ kind: pb.playing ? "pause" : "play", at: target });
  }, []);

  const intendSeek = useCallback((at: BookPos) => {
    const o = live.current;
    if (!o.canControl) return;
    suppress.current.open(SUPPRESS_SEEK_MS, Date.now());
    o.control({ kind: "seek", at });
  }, []);

  const intendRate = useCallback((rate: number) => {
    const o = live.current;
    if (!o.canControl) return;
    o.control({ kind: "rate", rate });
  }, []);

  /**
   * The engine crossed into a new page on its own, or a read-room controller deliberately turned
   * forward. Tell the room, so its `at` follows the voice/reading anchor respectively.
   *
   * ⚠️ **Guarded by `canControl`, and it must be.** In a `controlMode: "host"` room every follower's
   * engine crosses the same boundary within a few hundred milliseconds of the host's, and four
   * members all asserting the new page would be four `advance` frames the server has to gate. The
   * one who holds the remote is the one whose engine speaks for the room; everybody else's crossing
   * is an observation, and the correction loop puts them on the room's answer a tick later.
   *
   * ⚠️ **Also guarded on the room being `playing`.** An `advance` is only news while the room is
   * actually reading. A deck that runs out its tail after somebody pressed pause is the engine
   * finishing a buffer, not the room turning a page.
   */
  const intendAdvance = useCallback((at: BookPos) => {
    const o = live.current;
    const pb = o.playback;
    if (pb === null || !o.canControl) return;
    // Listen advances only while the shared clock is moving. Read mode has no clock: a controller's
    // deliberate forward page turn is the advance, and followers are moved by the resulting sid.
    if (pb.mode === "listen" && !pb.playing) return;
    // No `Suppressor` window here: `advance` changes the room's `sid`, not this element's clock, and
    // the sync frame it produces is the one the loop is supposed to act on.
    o.control({ kind: "advance", at });
  }, []);

  return {
    needsGesture,
    tapToJoin,
    resync,
    snapSid,
    blocking,
    blockedBy: opts.playback?.blockedBy ?? null,
    intendPlayPause,
    intendSeek,
    intendRate,
    intendAdvance,
  };
}

/** Re-exported so a screen can validate a `BookPos` off the wire without importing two modules. */
export { isBookPos };
